import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import Title from "../../extra/Title";
import { apiInstanceFetch } from "../../../util/api";

const INK = "#1A1614";
const MUTED = "#8A857F";
const HAIRLINE = "#E8E1D3";
const GOLD = "#D4A129";
const CREAM = "#FFFDF7";
const RED = "#DC2626";
const REDBG = "#FDECEC";

const STEP_LABEL = {
  pick: "Choosing video",
  persist: "Copying file",
  size: "File too large",
  compress: "Compressing",
  thumbnail: "Thumbnail",
  upload: "Sending",
  server: "Server refused",
  other: "Other",
};

const mb = (b) => (b == null ? "—" : `${(b / 1048576).toFixed(1)} MB`);

// Phone-side reel upload failures reported by the app (2026-09-22). Before
// this page the server only ever saw completed requests, so "upload fails on
// Android" complaints had no evidence behind them.
const UploadFailures = () => {
  const [data, setData] = useState({ rows: [], total: 0, byStep: [], byPlatform: [], byVersion: [] });
  const [days, setDays] = useState(7);
  const [step, setStep] = useState("");
  const [platform, setPlatform] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    apiInstanceFetch
      .get(`uploadFailure/admin/list?days=${days}&limit=200${step ? `&step=${step}` : ""}${platform ? `&platform=${platform}` : ""}`)
      .then((r) => setData({ rows: r.rows || [], total: r.total || 0, byStep: r.byStep || [], byPlatform: r.byPlatform || [], byVersion: r.byVersion || [] }))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, step, platform]);

  const userName = (u) => {
    if (!u) return "—";
    return ((u.firstName || "") + " " + (u.lastName || "")).trim() || u.email || "—";
  };

  const chip = (label, n, active, onClick) => (
    <button
      key={label}
      onClick={onClick}
      className="btn btn-sm"
      style={{
        border: `1px solid ${active ? GOLD : HAIRLINE}`,
        background: active ? "#FFF6D6" : "#fff",
        color: INK,
        fontWeight: 600,
        marginRight: 6,
        marginBottom: 6,
      }}
    >
      {label} <span style={{ color: MUTED }}>({n})</span>
    </button>
  );

  return (
    <div className="mainCategory">
      <Title name="Upload Failures" />

      <div className="d-flex align-items-center flex-wrap" style={{ gap: 10, margin: "16px 0" }}>
        <i className="fa-solid fa-triangle-exclamation" style={{ color: RED }} />
        <span style={{ fontWeight: 700, color: INK, fontSize: 15 }}>
          {data.total} failed upload{data.total === 1 ? "" : "s"} in the last
        </span>
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="form-select form-select-sm"
          style={{ width: 120, border: `1px solid ${HAIRLINE}` }}
        >
          <option value={1}>24 hours</option>
          <option value={3}>3 days</option>
          <option value={7}>7 days</option>
          <option value={30}>30 days</option>
          <option value={90}>90 days</option>
        </select>
        {loading && <span style={{ color: MUTED, fontSize: 12 }}>loading…</span>}
      </div>

      <div style={{ marginBottom: 6 }}>
        <span style={{ color: MUTED, fontSize: 12, marginRight: 8 }}>Where it failed:</span>
        {chip("All steps", data.byStep.reduce((a, s) => a + s.n, 0), step === "", () => setStep(""))}
        {data.byStep.map((s) => chip(STEP_LABEL[s._id] || s._id, s.n, step === s._id, () => setStep(step === s._id ? "" : s._id)))}
      </div>
      <div style={{ marginBottom: 14 }}>
        <span style={{ color: MUTED, fontSize: 12, marginRight: 8 }}>Platform:</span>
        {chip("All", data.byPlatform.reduce((a, s) => a + s.n, 0), platform === "", () => setPlatform(""))}
        {data.byPlatform.map((s) => chip(s._id || "unknown", s.n, platform === s._id, () => setPlatform(platform === s._id ? "" : s._id)))}
        {data.byVersion.length > 0 && (
          <span style={{ color: MUTED, fontSize: 12, marginLeft: 12 }}>
            Top versions: {data.byVersion.map((v) => `${v._id.platform || "?"} ${v._id.appVersion || "?"} (${v.n})`).join(" · ")}
          </span>
        )}
      </div>

      <div style={{ background: "#fff", border: `1px solid ${HAIRLINE}`, borderRadius: 14, overflow: "auto" }}>
        <table className="table mb-0" style={{ fontSize: 13 }}>
          <thead style={{ background: CREAM }}>
            <tr style={{ color: MUTED }}>
              <th>When</th>
              <th>User</th>
              <th>Phone</th>
              <th>App</th>
              <th>Flow</th>
              <th>Failed at</th>
              <th>Video</th>
              <th>HTTP</th>
              <th>Error</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ textAlign: "center", padding: 32, color: MUTED }}>
                  No failures reported for this period
                </td>
              </tr>
            ) : (
              data.rows.map((r) => (
                <tr key={r._id}>
                  <td style={{ whiteSpace: "nowrap" }}>{dayjs(r.createdAt).format("DD MMM HH:mm")}</td>
                  <td>{userName(r.userId)}</td>
                  <td>
                    <div style={{ fontWeight: 600 }}>{r.deviceModel || "—"}</div>
                    <div style={{ color: MUTED, fontSize: 12 }}>{[r.platform, r.osVersion].filter(Boolean).join(" ")}</div>
                  </td>
                  <td>{r.appVersion || "—"}</td>
                  <td>{r.flow || "—"}</td>
                  <td>
                    <span style={{ background: REDBG, color: RED, padding: "2px 8px", borderRadius: 10, fontWeight: 700, fontSize: 12 }}>
                      {STEP_LABEL[r.step] || r.step}
                    </span>
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {mb(r.fileBytes)}
                    {r.compressedBytes != null && <span style={{ color: MUTED }}> → {mb(r.compressedBytes)}</span>}
                    {r.durationSec != null && <div style={{ color: MUTED, fontSize: 12 }}>{r.durationSec}s{r.elapsedMs != null ? ` · took ${Math.round(r.elapsedMs / 1000)}s` : ""}</div>}
                  </td>
                  <td>{r.httpStatus || "—"}</td>
                  <td style={{ maxWidth: 420, wordBreak: "break-word", color: INK }}>{r.error || "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UploadFailures;
